import React, { useState, useEffect, useRef } from "react";
import { FedaCheckoutButton } from "fedapay-reactjs";
import { initiatePayment } from "../../services/invoices";

interface FedaPayButtonProps {
  invoiceId: number;
  amount: number;
  customer: {
    firstName: string;
    lastName: string;
    email?: string;
    phoneNumber: string;
  };
  description: string;
  onPaymentSuccess?: () => void;
  onPaymentError?: (error: any) => void;
  disabled?: boolean;
}

const FedaPayButton: React.FC<FedaPayButtonProps> = ({
  invoiceId,
  amount,
  customer,
  description,
  onPaymentSuccess,
  onPaymentError,
  disabled = false,
}) => {
  const [paymentData, setPaymentData] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // `customer` is rebuilt as a literal `{...}` at the callsite (TabThree
  // re-renders pass a fresh reference each time) and `onPaymentError` is
  // an inline arrow — including them in the effect deps re-fires
  // `initiatePayment` on every parent render, racking up duplicate
  // POST /invoice/initiate-payment calls. Read the latest values via
  // refs and depend only on the primitives that should actually
  // re-initiate (invoiceId / amount / description / disabled).
  const customerRef = useRef(customer);
  const onPaymentErrorRef = useRef(onPaymentError);
  useEffect(() => {
    customerRef.current = customer;
    onPaymentErrorRef.current = onPaymentError;
  });

  // Automatically initiate payment when component mounts
  useEffect(() => {
    if (disabled || !invoiceId || amount <= 0) {
      setLoading(false);
      return;
    }

    const handleInitiatePayment = async () => {
      setLoading(true);
      setError(null);

      try {
        const response = await initiatePayment(invoiceId);
        if (response.ok && response.data) {
          // Use customer data from props, fallback to response data if props are empty
          const currentCustomer = customerRef.current;
          const customerData = response.data.customer || {};
          const firstName = currentCustomer.firstName || customerData.firstName || "";
          const lastName = currentCustomer.lastName || customerData.lastName || "";
          const email = currentCustomer.email || customerData.email || "";
          const phoneNumber = currentCustomer.phoneNumber || customerData.phoneNumber || "";

          // Build customer object - FedaPay expects all fields even if empty
          const finalCustomer = {
            firstname: firstName,
            lastname: lastName,
            email: email,
            phone_number: {
              number: phoneNumber,
              country: "bj", // Benin country code
            },
          };

          setPaymentData({
            amount: amount, // Strictly use the amount prop passed from parent
            currency: response.data.currency || "XOF",
            description: response.data.description || description,
            callback_url: `${window.location.origin}/payment-success`,
            cancel_url: window.location.href,
            metadata: {
              invoiceId: invoiceId.toString(),
            },
            customer: finalCustomer,
          });
        } else {
          throw new Error(
            response.message || "Le paiement n'a pas pu être initié."
          );
        }
      } catch (err: any) {
        setError(err.message || "Le paiement n'a pas pu être initié.");
        const cb = onPaymentErrorRef.current;
        if (cb) {
          cb(err);
        }
      } finally {
        setLoading(false);
      }
    };

    handleInitiatePayment();
  }, [disabled, invoiceId, amount, description]);

  const handlePaymentSuccess = async (transaction: any) => {
    // Call parent callback which will reload the page
    if (onPaymentSuccess) {
      onPaymentSuccess();
    }
  };

  const handlePaymentError = (error: any) => {
    setError(error.message || "Le paiement a échoué.");
    if (onPaymentError) {
      onPaymentError(error);
    }
  };

  if (disabled) {
    return (
      <button
        disabled
        className="bg-gray-400 cursor-not-allowed text-white text-sm font-medium px-6 py-2.5 rounded-md"
      >
        Payé
      </button>
    );
  }

  // Show loading state while initiating payment
  if (loading || !paymentData) {
    return (
      <div>
        <button
          disabled
          className="bg-green-500 hover:bg-green-600 disabled:bg-gray-400 disabled:cursor-not-allowed shadow-md text-white text-sm font-medium px-6 py-2.5 rounded-md transition-all duration-300"
        >
          {loading ? "Chargement…" : "Payer avec Mobile Money"}
        </button>
        {error && (
          <div className="text-red-500 text-xs mt-2">{error}</div>
        )}
      </div>
    );
  }

  const checkoutOptions = {
    public_key: import.meta.env.VITE_FEDAPAY_PUBLIC_KEY || "",
    transaction: {
      amount: paymentData.amount,
      description: paymentData.description,
      callback_url: paymentData.callback_url,
      cancel_url: paymentData.cancel_url,
      metadata: paymentData.metadata,
      customer: paymentData.customer, // Customer in transaction
    },
    customer: paymentData.customer, // Also at root level (some FedaPay versions expect this)
    currency: {
      iso: paymentData.currency || "XOF",
    },
    button: {
      class: "bg-green-500 hover:bg-green-600 shadow-md text-white text-sm font-medium px-6 py-2.5 rounded-md transition-all duration-300",
      text: "Payer avec Mobile Money",
    },
    onComplete: (resp: any) => {
      const FedaPay = (window as any)["FedaPay"];
      if (resp.reason === FedaPay?.DIALOG_DISMISSED) {
        handlePaymentError(new Error("Paiement annulé."));
      } else if (resp.transaction) {
        handlePaymentSuccess(resp.transaction);
      } else {
        handlePaymentError(new Error(resp.reason || "Le paiement a échoué."));
      }
    },
  };

  return (
    <div>
      <FedaCheckoutButton options={checkoutOptions} />
    </div>
  );
};

export default FedaPayButton;

